import { useEffect, useState } from 'react';
import {
  FileSignature, Send, Check, Clock, Download, Plus, Sparkles, AlertTriangle,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { date, dateTime } from '@/lib/format';
import { saveFile } from '@/lib/download';
import {
  Badge, Button, Card, CardHeader, EmptyState, Field, Input, LoadingBlock, Modal,
  PageHeader, Table, TableWrap, Td, Textarea, Th, useToast,
} from '@/components/ui';
import type { Agreement, AgreementsResponse } from '@shared/types';

/**
 * Tenancy agreements.
 *
 * Write it once, publish it, and every tenant is asked to sign in their portal
 * by typing their name and the date. Change it and everybody affected is told
 * what changed and asked again.
 *
 * The one thing this screen has to be honest about is what a signature here is
 * worth: it records that somebody signed in to their own account — which took
 * a code sent to their phone — and typed their name. That is the weight of
 * agreeing to terms on a website. Real, relied on daily, and not a witnessed
 * signature. Anybody who needs to know that needs to know it before they
 * publish, not in a tribunal.
 */
export default function Agreements() {
  const { can } = useAuth();
  const toast = useToast();
  const { data, loading, reload } = useApi<AgreementsResponse>('/agreements');

  const [editing, setEditing] = useState<Agreement | 'new' | null>(null);
  const [publishing, setPublishing] = useState<Agreement | null>(null);
  const [busy, setBusy] = useState(false);

  const live = data?.agreements.find((a) => a.status === 'published') ?? null;
  const drafts = data?.agreements.filter((a) => a.status === 'draft') ?? [];
  const archived = data?.agreements.filter((a) => a.status === 'archived') ?? [];

  async function remind(tenantId: string, tenantName: string) {
    try {
      const res = await api.post('/agreements/remind', { tenant_id: tenantId });
      if (res.delivered) toast.success(`Reminded ${tenantName}`);
      else toast.info(`Could not reach ${tenantName}: ${(res.problems ?? []).join('; ')}`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send that reminder');
    }
  }

  async function downloadSigned(signatureId: string) {
    try {
      saveFile(await api.post('/agreements/pdf', { signature_id: signatureId }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build that PDF');
    }
  }

  async function publish() {
    if (!publishing) return;
    setBusy(true);
    try {
      const res = await api.post(`/agreements/${publishing.id}/publish`, {
        change_note: publishing.change_note ?? '',
      });
      setPublishing(null);
      reload();
      toast.success(
        res.notified > 0
          ? `Published. ${res.notified} of ${res.asked} tenants have been told.`
          : `Published, and ${res.asked} tenants will be asked to sign when they sign in.`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not publish that version');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <LoadingBlock rows={6} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenancy agreements"
        subtitle="Write it once. Your tenants sign it in their portal."
        action={can('owner') && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            New version
          </Button>
        )}
      />

      {/*
        Said once, near the top, where somebody deciding whether to rely on this
        will read it. Not buried in help: the landlord who needs it is the one
        about to publish.
      */}
      <div className="flex items-start gap-2.5 text-sm text-muted bg-raised/60 border border-line
                      rounded-xl px-4 py-3 leading-relaxed">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-warn" />
        <p>
          A tenant signs by typing their name and today’s date in their portal, from an account
          secured by a code sent to their own phone. That is the same weight as agreeing to terms
          on a website — widely relied on, and not a witnessed or certificate-backed signature.
          What your agreement has to contain is set by the law where you let, and that part is
          yours.
        </p>
      </div>

      {live ? (
        <Card>
          <CardHeader
            title={`${live.name} · version ${live.version}`}
            subtitle={`Published ${live.published_at ? date(live.published_at) : ''} · ${live.signed_count} signed, ${live.pending_count} waiting`}
            action={<Badge tone="ok">Live</Badge>}
          />
          <div className="p-5 sm:p-6 pt-4 space-y-4">
            {live.change_note && (
              <p className="text-sm text-muted">
                <span className="text-ink">What changed:</span> {live.change_note}
              </p>
            )}
            <pre className="text-sm whitespace-pre-wrap leading-relaxed bg-raised/50 border
                            border-line rounded-xl p-4 max-h-64 overflow-y-auto font-sans">
              {live.body}
            </pre>

            {(live.signatures ?? []).length > 0 && (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Tenant</Th>
                      <Th>State</Th>
                      <Th>Signed</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {(live.signatures ?? []).map((s) => (
                      <tr key={s.id}>
                        <Td>{s.tenant_name}</Td>
                        <Td>
                          {s.status === 'signed' ? (
                            <Badge tone="ok"><Check className="h-3 w-3" /> Signed</Badge>
                          ) : (
                            <Badge tone="warn"><Clock className="h-3 w-3" /> Waiting</Badge>
                          )}
                        </Td>
                        <Td className="text-muted">
                          {s.signed_at ? dateTime(s.signed_at) : '—'}
                        </Td>
                        <Td className="text-right">
                          {s.status === 'signed' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Download className="h-3.5 w-3.5" />}
                              onClick={() => void downloadSigned(s.id)}
                            >
                              PDF
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Send className="h-3.5 w-3.5" />}
                              onClick={() => void remind(s.tenant_id, s.tenant_name ?? 'them')}
                            >
                              Remind
                            </Button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}

            {data.outstanding.length > 0 && (
              <div className="text-sm text-muted">
                <p className="mb-2">
                  Still to sign: {data.outstanding.map((o) => o.tenant_name).join(', ')}.
                </p>
                {data.outstanding.some((o) => !o.portal_enabled) && (
                  <p className="text-warn">
                    Some of them have no portal access, so they cannot sign. Switch it on from
                    their record first.
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>
      ) : (
        <EmptyState
          icon={<FileSignature className="h-6 w-6" />}
          title="No agreement published yet"
          message="Write one, or start from a skeleton and make it yours."
          action={can('owner') && (
            <Button variant="primary" onClick={() => setEditing('new')}>Write one</Button>
          )}
        />
      )}

      {drafts.length > 0 && (
        <Card>
          <CardHeader title="Drafts" subtitle="Nobody sees these until you publish" />
          <div className="p-5 sm:p-6 pt-4 space-y-3">
            {drafts.map((d) => (
              <div key={d.id} className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[12rem]">
                  <p className="text-sm">{d.name} · version {d.version}</p>
                  <p className="text-xs text-faint">Last changed {dateTime(d.updated_at)}</p>
                </div>
                <Button size="sm" onClick={() => setEditing(d)}>Edit</Button>
                {can('owner') && (
                  <Button size="sm" variant="primary" onClick={() => setPublishing(d)}>
                    Publish
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {archived.length > 0 && (
        <Card>
          <CardHeader
            title="Earlier versions"
            subtitle="Kept, because they are what somebody agreed to at the time"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-2">
            {archived.map((a) => (
              <p key={a.id} className="text-sm text-muted">
                {a.name} · version {a.version} ·{' '}
                {a.signed_count} signed ·{' '}
                {a.published_at ? `published ${date(a.published_at)}` : 'never published'}
              </p>
            ))}
          </div>
        </Card>
      )}

      {editing && (
        <EditorModal
          agreement={editing === 'new' ? null : editing}
          starter={data.starter}
          placeholders={data.placeholders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      <Modal
        open={Boolean(publishing)}
        onClose={() => setPublishing(null)}
        title="Publish this version?"
        footer={(
          <>
            <Button onClick={() => setPublishing(null)}>Cancel</Button>
            <Button variant="primary" loading={busy} onClick={() => void publish()}>
              Publish and ask everyone to sign
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted leading-relaxed">
            Every tenant with a current tenancy will be asked to sign this version, and told
            about it in whichever way they have asked to be contacted. Anything they signed
            before stays on the record.
          </p>
          <p className="text-sm text-muted leading-relaxed">
            Once published it cannot be edited — people will have signed it. Changing it later
            means another version and another round of signatures.
          </p>
          <Field
            label="What changed"
            hint="Shown to tenants when you ask them to sign again. Leave blank on a first version."
          >
            <Input
              value={publishing?.change_note ?? ''}
              onChange={(e) => setPublishing((p) => (p ? { ...p, change_note: e.target.value } : p))}
              placeholder="Added a no-smoking clause"
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

function EditorModal({ agreement, starter, placeholders, onClose, onSaved }: {
  agreement: Agreement | null;
  starter: string;
  placeholders: { key: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(agreement?.name ?? 'Tenancy agreement');
  const [body, setBody] = useState(agreement?.body ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(agreement?.name ?? 'Tenancy agreement');
    setBody(agreement?.body ?? '');
  }, [agreement]);

  async function save() {
    setBusy(true);
    try {
      if (agreement) await api.patch(`/agreements/${agreement.id}`, { name, body });
      else await api.post('/agreements', { name, body });
      toast.success('Draft saved');
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save that draft');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={agreement ? `Edit version ${agreement.version}` : 'New version'}
      size="lg"
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Save draft
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field
          label="The agreement"
          hint="Anything in double braces is filled in with that tenant's own details when they read it."
        >
          <Textarea
            rows={16}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="font-mono text-xs"
            placeholder="Write your agreement here…"
          />
        </Field>

        {body.trim().length === 0 && (
          <Button
            size="sm"
            icon={<Sparkles className="h-3.5 w-3.5" />}
            onClick={() => setBody(starter)}
          >
            Start from a skeleton
          </Button>
        )}

        <div>
          <p className="text-xs text-faint mb-2">
            Click to insert. Anything you do not use is simply left out.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((p) => (
              <button
                key={p.key}
                type="button"
                title={p.label}
                onClick={() => setBody((b) => `${b}{{${p.key}}}`)}
                className="text-[11px] px-2 py-1 rounded-md bg-raised text-muted
                           hover:text-ink border border-line"
              >
                {`{{${p.key}}}`}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
